import { mapValues, values } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { from, Subscription } from 'rxjs';
import { ProjectId } from 'api/models';
import { TaskAPI } from 'api/tasks';
import { RootState } from 'data';
import { taskNormalizer } from 'data/tasks/schema';
import { logger } from 'services';
import { FieldsStore, TaskField } from 'stores/fields';
import { ProjectsStore } from 'stores/projects';
import { loadedAllTasks, loadingAllTasks } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { queueMacrotask } from 'utils/macrotask';
import { toStoreTask } from 'utils/to-store-task';

export const useAllTasksRefresh = (
  onRequest?: () => void,
  onComplete?: () => void,
  shouldEnable = true,
) => {
  const dispatch = useDispatch();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const [initialized, setInitialized] = useState(false);
  const fields_lookup = useMemo(
    () =>
      mapValues(fieldsStore.byId, ([, x]) => x!) as Record<number, TaskField>,
    [fieldsStore.byId],
  );
  const projectsStore = useSelector(
    ({ projects_v2 }: RootState) => projects_v2,
  );

  useEffect(() => {
    if (!shouldEnable) {
      return undefined;
    }
    if (
      initialized ||
      !FieldsStore.loaded(fieldsStore) ||
      FieldsStore.refreshing(fieldsStore) ||
      !ProjectsStore.loaded(projectsStore) ||
      ProjectsStore.refreshing(projectsStore)
    ) {
      return undefined;
    }

    const subscriptions: Subscription[] = [];
    const projectIds = ProjectsStore.toList(projectsStore).map(
      ({ projectId }) => ProjectId(projectId),
    );

    onRequest?.();

    dispatch(loadingAllTasks(projectIds));
    subscriptions.push(
      from(TaskAPI.getAllTasksLight()).subscribe(
        (apiTasks) => {
          const updatedTasks = apiTasks.map((task) => ({
            ...task,
            fields: task.fields.map(withValidCustomFieldsForData),
          }));
          const {
            entities: { tasks },
          } = taskNormalizer(updatedTasks);
          const to_row = toStoreTask(fields_lookup, tasks);

          dispatch(loadedAllTasks(values(tasks).map(to_row)));
        },
        (error) => {
          logger.error('failed to load all tasks', { error });
        },
        () => {
          setInitialized(true);
          queueMacrotask(() => onComplete?.());
        },
      ),
    );

    return () => {
      logger.debug('unsubscribing from all tasks api handling');
      subscriptions.forEach((s) => s.unsubscribe());
    };
  }, [
    fieldsStore,
    fields_lookup,
    dispatch,
    projectsStore,
    initialized,
    setInitialized,
    onRequest,
    onComplete,
    shouldEnable,
  ]);
};
